import React from 'react';
import { Modal } from 'antd';
import { Button } from '@/components/Button';
import styles from './HtsQuestionnaire.module.scss';

/** One question whose on-screen value hasn't been persisted yet — see HtsQuestionnaire's `unsavedQuestions`. */
export interface HtsUnsavedQuestion {
  id: string;
  /** 1-based position in the stepper, so the row matches the progress indicator the candidate has been watching. */
  number: number;
  text: string;
}

interface HtsExitModalProps {
  /**
   * Questions whose live value differs from what's on the server. Listed by
   * name rather than summarised in prose: a sentence has to stay vague about
   * *which* answers are affected, which reads as "none of my work is saved."
   * Empty means everything is already persisted.
   */
  unsavedQuestions: HtsUnsavedQuestion[];
  onStay: () => void;
  /** Navigates away without saving anything — see HtsQuestionnaire's `handleExit`. */
  onExit: () => void;
}

export const HtsExitModal: React.FC<HtsExitModalProps> = ({ unsavedQuestions, onStay, onExit }) => (
  <Modal open closable={false} maskClosable={false} footer={null} width={420} centered>
    <h3 className={styles.confirmTitle}>Exit questionnaire?</h3>
    {unsavedQuestions.length > 0 ? (
      <>
        <p className={styles.confirmBodyTight}>These answers have unsaved changes and won&apos;t be kept:</p>
        <ul className={styles.unsavedList}>
          {unsavedQuestions.map((question) => (
            <li key={question.id} className={styles.unsavedItem}>
              <span className={styles.unsavedItemNumber}>Q{question.number}</span>
              <span className={styles.unsavedItemText}>{question.text}</span>
            </li>
          ))}
        </ul>
      </>
    ) : (
      <p className={styles.confirmBody}>
        Your progress is saved — pick up right where you left off next time.
      </p>
    )}
    <div className={styles.confirmActions}>
      <Button appearance="outline" onClick={onStay}>Keep answering</Button>
      <Button appearance="solid" accent="green" onClick={onExit}>
        {unsavedQuestions.length > 0 ? 'Exit anyway' : 'Exit'}
      </Button>
    </div>
  </Modal>
);
